import type {
  ActivityDetail,
  ActivityDuplicateRef,
  KeepBothRequest,
  KeepBothResponse,
  ActivitiesListResponse,
  Activity,
  ActivityKind,
  ActivityExclusionReason,
  ImportPreferencesResponse,
  RemovedActivityGroup,
  RemovedActivitiesResponse,
  BulkDeleteRequest,
  BulkDeleteResponse,
  RestoreRemovedRequest,
  RestoreRemovedResponse,
  ActivityShareData,
  ActivityShareFormat,
  ActivityShareStyle,
  ActivityShareUrlResponse,
} from "@atlas/shared";
import { apiDelete, apiGet, apiPatch, apiPost } from "../lib/api-client";

export type { Activity, ActivityDetail, ActivitiesListResponse } from "@atlas/shared";

// ─── Import review (INT-2) ────────────────────────────────────────────────────

export type ReviewItemType = "peak" | "landmark" | "trail_section";

export interface ReviewItemRef {
  itemType: ReviewItemType;
  itemId: string;
}

export interface ReviewPeak {
  id: string;
  name: string;
  slug: string;
  elevationM: number;
  latitude: number;
  longitude: number;
  distanceM: number;
  /** Matched items only (spec 28): this activity first collected it. */
  isNew?: boolean;
  /** Matched items only: which visit this was (1 = first). 0 on near misses. */
  visitNumber?: number;
}

export interface ReviewLandmark {
  id: string;
  name: string;
  slug: string;
  category: string;
  latitude: number;
  longitude: number;
  distanceM: number;
  /** Matched items only (spec 28): this activity first collected it. */
  isNew?: boolean;
  /** Matched items only: which visit this was (1 = first). 0 on near misses. */
  visitNumber?: number;
}

export interface ReviewSection {
  id: string;
  trailId: string;
  trailName: string;
  sectionNumber: number;
  name: string | null;
  distanceKm: number | null;
  coveragePct: number;
  geojson: { type: "LineString"; coordinates: [number, number][] } | null;
}

export interface ReviewOverride {
  id: string;
  itemType: string;
  itemId: string;
  action: string;
  createdAt: string;
}

export interface ActivityReviewResponse {
  activity: {
    id: string;
    name: string;
    sourceType: string;
    sourceId: string | null;
    startedAt: string;
    durationSeconds: number | null;
    distanceM: number | null;
    elevationGainM: number | null;
    processedAt: string | null;
    geojson: { type: "LineString"; coordinates: [number, number][] } | null;
    /** The trip this activity belongs to (spec 35). */
    trip?: { id: string; name: string } | null;
    /** Spec 37: parked duplicates of this walk from other sources. */
    duplicates?: ActivityDuplicateRef[];
  };
  matched: { peaks: ReviewPeak[]; landmarks: ReviewLandmark[]; sections: ReviewSection[] };
  nearMisses: { peaks: ReviewPeak[]; landmarks: ReviewLandmark[]; sections: ReviewSection[] };
  overrides: ReviewOverride[];
}

export function fetchActivityReview(id: string): Promise<ActivityReviewResponse> {
  return apiGet(`/activities/${id}/review`);
}

export function saveActivityReview(
  id: string,
  changes: { include: ReviewItemRef[]; exclude: ReviewItemRef[] },
): Promise<ActivityReviewResponse> {
  return apiPost(`/activities/${id}/review`, changes);
}

export function fetchActivity(id: string): Promise<ActivityDetail> {
  return apiGet(`/activities/${id}`);
}

export function fetchActivities(
  limit = 20,
  offset = 0,
  source?: string,
  kind?: ActivityKind,
): Promise<ActivitiesListResponse> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (source) params.set("source", source);
  if (kind) params.set("kind", kind);
  return apiGet(`/activities?${params}`);
}

export function updateActivity(
  id: string,
  patch: { name?: string; startedAt?: string; endedAt?: string | null },
): Promise<{ activity: Activity }> {
  return apiPatch(`/activities/${id}`, patch);
}

export function deleteActivity(id: string): Promise<{ ok: boolean }> {
  return apiDelete(`/activities/${id}`);
}

// ─── Kinds, import preferences, bulk delete, removed list (spec 26) ──────────

export type {
  ActivityKind,
  ActivityExclusionReason,
  ImportPreferencesResponse,
  RemovedActivity,
  RemovedActivityGroup,
  RemovedActivitiesResponse,
  BulkDeleteRequest,
  BulkDeleteResponse,
  RestoreRemovedRequest,
  RestoreRemovedResponse,
} from "@atlas/shared";

export function bulkDeleteActivities(body: BulkDeleteRequest): Promise<BulkDeleteResponse> {
  return apiPost(`/activities/bulk-delete`, body);
}

export function fetchImportPreferences(): Promise<ImportPreferencesResponse> {
  return apiGet(`/activities/import-preferences`);
}

export function updateImportPreferences(excludedKinds: ActivityKind[]): Promise<ImportPreferencesResponse> {
  return apiPatch(`/activities/import-preferences`, { excludedKinds });
}

export function fetchRemovedActivities(
  opts: {
    reason?: ActivityExclusionReason;
    kind?: ActivityKind;
    source?: string;
    limit?: number;
    offset?: number;
  } = {},
): Promise<RemovedActivitiesResponse> {
  const params = new URLSearchParams();
  if (opts.reason) params.set("reason", opts.reason);
  if (opts.kind) params.set("kind", opts.kind);
  if (opts.source) params.set("source", opts.source);
  params.set("limit", String(opts.limit ?? 50));
  params.set("offset", String(opts.offset ?? 0));
  return apiGet(`/activities/removed?${params}`);
}

export function restoreRemovedActivities(body: RestoreRemovedRequest): Promise<RestoreRemovedResponse> {
  return apiPost(`/activities/removed/restore`, body);
}

// ─── Public activity share ───────────────────────────────────────────────────

export type {
  ActivityShareData,
  ActivityShareFormat,
  ActivityShareStyle,
  ActivityShareUrlResponse,
} from "@atlas/shared";

/** Mints the signed public share links for one of the user's own activities. */
export function fetchActivityShareUrl(id: string, identity: boolean): Promise<ActivityShareUrlResponse> {
  return apiGet(`/activities/${id}/share-url${identity ? "?identity=1" : ""}`);
}

/** Loads the sig-gated public payload behind a share link (no session needed). */
export function fetchActivityShare(id: string, sig: string, identity: boolean): Promise<ActivityShareData> {
  const params = new URLSearchParams({ sig });
  if (identity) params.set("i", "1");
  return apiGet(`/share/activity/${id}.json?${params}`);
}

/** Builds a share-image URL from the minted `imageUrl` base. */
export function activityShareImageUrl(
  imageUrl: string,
  opts: { format: ActivityShareFormat; style: ActivityShareStyle; route?: boolean },
): string {
  const url = new URL(imageUrl);
  url.searchParams.set("format", opts.format);
  url.searchParams.set("style", opts.style);
  if (opts.route === false) url.searchParams.set("route", "0");
  return url.toString();
}

// ─── Duplicates (spec 37) ───────────────────────────────────────────────────

/** Restore a parked duplicate of this activity and never park the pair again. */
export function keepBothCopies(activityId: string, exclusionId: string): Promise<KeepBothResponse> {
  return apiPost(`/activities/${activityId}/keep-both`, { exclusionId } satisfies KeepBothRequest);
}
